import React from 'react';
import { Link } from 'react-router-dom';
import { Box, Container, Typography, Card, CardContent, Button, Chip } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';

const Pricing: React.FC = () => {
  const plans = [
    {
      name: 'Free',
      price: '$0',
      period: '/month',
      description: 'Perfect for testing and small projects',
      features: [
        '100 screenshots/month',
        'Basic customization',
        'Standard resolution'
      ],
      popular: false
    },
    {
      name: 'Pro',
      price: '$19',
      period: '/month',
      description: 'Ideal for growing businesses',
      features: [
        '100,000 screenshots/month',
        'Advanced customization',
        'Priority support',
        'Cloud bucket integration',
        'Custom headers'
      ],
      popular: true
    },
    {
      name: 'Enterprise',
      price: 'Custom',
      period: '',
      description: 'For large-scale operations',
      features: [
        'Unlimited screenshots',
        'Full customization',
        'Dedicated support',
        'Custom SLA',
        'On-premise deployment',
        'Advanced analytics',
        'Team management',
        'Custom integrations'
      ],
      popular: false
    }
  ];

  return (
    <Box sx={{ bgcolor: '#f5f5f5', minHeight: 'calc(100vh - 64px - 300px)', py: { xs: 4, sm: 6, md: 8 } }}>
      <Container maxWidth="lg">
        <Typography variant="h3" align="center" gutterBottom fontWeight={700} sx={{ fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' } }}>
          Simple, Transparent Pricing
        </Typography>
        <Typography variant="h6" align="center" color="text.secondary" sx={{ mb: 6 }}>
          Choose the plan that's right for you
        </Typography>

        <Box sx={{ 
          display: 'grid', 
          gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
          gap: 4,
          mb: 8
        }}>
          {plans.map((plan, index) => (
            <Card key={index} 
                sx={{ 
                  height: '100%',
                  position: 'relative',
                  border: plan.popular ? '2px solid black' : 'none',
                  transform: { xs: 'scale(1)', md: plan.popular ? 'scale(1.05)' : 'scale(1)' },
                  transition: 'transform 0.2s',
                  '&:hover': { transform: { xs: 'translateY(-4px)', md: plan.popular ? 'scale(1.05)' : 'translateY(-8px)' } }
                }}
              >
                {plan.popular && (
                  <Chip 
                    label="Most Popular" 
                    sx={{ 
                      position: 'absolute', 
                      top: 16, 
                      right: 16,
                      bgcolor: 'black',
                      color: 'white',
                      fontWeight: 600,
                      fontSize: { xs: '0.75rem', sm: '0.8125rem' }
                    }} 
                  />
                )}
                <CardContent sx={{ p: { xs: 3, sm: 4 }, display: 'flex', flexDirection: 'column', height: '100%' }}>
                  <Typography variant="h5" gutterBottom fontWeight={700} sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
                    {plan.name}
                  </Typography>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="h3" component="span" fontWeight={800} sx={{ fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' } }}>
                      {plan.price}
                    </Typography>
                    <Typography variant="body1" component="span" color="text.secondary">
                      {plan.period}
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    {plan.description}
                  </Typography>
                  
                  <Box sx={{ flexGrow: 1 }}>
                    {plan.features.map((feature, idx) => (
                      <Box key={idx} sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                        <CheckIcon sx={{ mr: 1, fontSize: 20 }} />
                        <Typography variant="body2">{feature}</Typography>
                      </Box>
                    ))}
                  </Box>

                  <Button 
                    component={Link}
                    to="/register"
                    variant={plan.popular ? "contained" : "outlined"}
                    fullWidth
                    size="large"
                    sx={{ mt: 3 }}
                  >
                    Get Started
                  </Button>
                </CardContent>
              </Card>
          ))}
        </Box>

        <Typography variant="h4" align="center" gutterBottom fontWeight={700} sx={{ mt: { xs: 6, md: 8 }, mb: 4, fontSize: { xs: '1.75rem', sm: '2rem', md: '2.125rem' } }}>
          Frequently Asked Questions
        </Typography>
        <Box sx={{ 
          display: 'grid', 
          gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
          gap: 3
        }}>
          {[
            { q: 'Can I change plans later?', a: 'Yes, you can upgrade or downgrade your plan at any time. Changes will be reflected in your next billing cycle.' },
            { q: 'What happens if I exceed my limit?', a: "We'll notify you when you're approaching your limit. You can upgrade anytime or purchase additional screenshots." },
            { q: 'Is there a contract?', a: 'No long-term contracts required. You can cancel your subscription at any time.' }
          ].map((faq, index) => (
            <Card key={index} sx={{ height: '100%' }}>
              <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                <Typography variant="h6" gutterBottom fontWeight={600} sx={{ fontSize: { xs: '1.125rem', sm: '1.25rem' } }}>
                  {faq.q}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {faq.a}
                </Typography>
              </CardContent>
            </Card>
          ))}
        </Box>
      </Container>
    </Box>
  );
};

export default Pricing;
